<!-- ModalDialog.vue -->
<script setup lang="ts">
    import { computed, watch, ref, onMounted, onBeforeUnmount } from "vue"
    import * as bootstrap from "bootstrap"
    import { ModalSize } from "../Enums/UiEnums";
    import { hasSlot } from '../lib/VueHelpers'


    const props = withDefaults(defineProps<{
            show?: boolean,
            size?: ModalSize,
            closeOnClickOutside?: boolean,
            closeOnEscapeKey?: boolean,
            showCloseButton?: boolean
        }>(), {
            show: false,
            size: ModalSize.Large,
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            showCloseButton: true
        });

    const theModal = ref<Element|undefined>();

    onMounted(() => {
        if (theModal?.value)
        {
            const modelEle = theModal.value;

            // Add event handlers
            modelEle.addEventListener("show.bs.modal", emitShow);
            modelEle.addEventListener("shown.bs.modal", emitShown);
            modelEle.addEventListener("hide.bs.modal", (evt) => emitHide(evt));
            modelEle.addEventListener("hidden.bs.modal", emitHidden);

            // Show the modal to start with?
            if (props.show)
                showModal();
        }
    });

    onBeforeUnmount(()=>{
        if (theModal?.value)
        {
            hideModal();

            const modelEle = theModal.value;
            modelEle.removeEventListener("show.bs.modal", emitShow);
            modelEle.removeEventListener("shown.bs.modal", emitShown);
            modelEle.removeEventListener("hide.bs.modal", emitHide);
            modelEle.removeEventListener("hidden.bs.modal", emitHidden);
        }
    });

    const showModal = () => {
        if (theModal?.value)
        {
            const thisModal = new bootstrap.Modal(theModal.value);
            thisModal.show();
        }
    }

    watch(() => props.show, (newVal, oldVal) => {
        // Show the dialog?
        if (true === newVal && false === oldVal)
        {
            showModal();
        }
        else if (false === newVal && true === oldVal)
        {
            hideModal();
        }
    });

    function hideModal() {
        if (!theModal.value)
            return;

        const thisModal = bootstrap.Modal.getInstance(theModal.value);
        if (thisModal)
        {
            thisModal.hide();        

        }
    }

    // Computed
    const hasFooter = computed(() => hasSlot("footer"));

    const modalDialogClass = computed(()=>
        [
            "modal-dialog",
            modalDialogSizeClass.value  
        ]);

    const modalDialogSizeClass = computed(()=> {
        switch (props.size)
        {
            case ModalSize.Small:
            return "modal-sm";

            case ModalSize.Medium:
            break;

            case ModalSize.Large:
            return "modal-lg";

            case ModalSize.XL:
            return "modal-xl";

            case ModalSize.FullScreen:
            return "modal-fullscreen";

            default:
            return "modal-lg";
        }
    });

    // Methods
    const emitShow = () =>{ emit("show"); }
    const emitShown = () =>{ emit("shown"); emitUpdateShowPropValue(true); }
    const emitHide = (evt: Event) => { emit("hide", evt); }
    const emitHidden = () =>{ emit("hidden"); emitUpdateShowPropValue(false); }
    const emitUpdateShowPropValue = (currentValue: boolean) => {  emit("update:show", currentValue); }

    // Emit events
    const emit = defineEmits<{
        (e: "show") : void, 
        (e: "shown"): void, 
        (e: "hide", evt:Event): void, 
        (e: "hidden"): void,
        (e: "update:show", currentValue: boolean): void
    }>();

    const dynamicProperties = computed(() =>{
        let properties: any = {};

        if (!props.closeOnClickOutside)
            properties["data-bs-backdrop"] = "static";
        if (!props.closeOnEscapeKey)
            properties["data-bs-keyboard"] = false;
        return properties;
    });

</script>

<template>
    <div class="modal fade" ref="theModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true" v-bind="dynamicProperties">
        <div :class="modalDialogClass">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="exampleModalLabel">
                        <slot name="title">Modal</slot>
                    </h5>
                    <button type="button" class="btn btn-close" data-bs-dismiss="modal" aria-label="Close" v-if="showCloseButton"></button>
                </div>
                <div class="modal-body">
                    <slot></slot>
                </div>
                <div class="modal-footer" v-if="hasFooter">
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </div>
</template>